import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { MultiSelect } from '@shared/models/question-types/multi-select';
import { TextMeasurementUtil } from '@shared/utils/text-measurement.util';
import { SafeHtmlPipe } from '../../../../../../shared/pipes/safe-html.pipe';

@Component({
  selector: 'app-multi-select-input',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ValidatorComponent,
    IconComponent,
    TranslateModule,
    SafeHtmlPipe,
  ],
  templateUrl: './multi-select-input.component.html',
  styleUrl: './multi-select-input.component.scss',
})
export class MultiSelectInputComponent implements AfterViewInit, OnDestroy {
  @ViewChild('statusMessageRef') statusMessageRef?: ElementRef<HTMLElement>;
  @Input({ required: true }) question!: MultiSelect;
  @Input({ required: true }) formGroup!: any;
  @Input() otherControl?: any;
  @Input({ required: true }) isOtherSelected!: boolean;
  @Input({ required: true }) onChange!: Function;
  @Input() onDisabledCheckboxClick?: Function;
  @Input() hasFormGroupBeenTouched!: Function;
  @Input({ required: true }) statusMessage!: string;
  @Input({ required: true }) columnTitle!: string;
  @Input() disabledCheckboxes: { [key: string]: boolean } = {};
  @Input() quotaErrorAlternativeIds?: Set<string>;
  ValidatorType = ValidatorType;
  Icon = Icon;

  scaleColumns: number = 1;

  private resizeObserver?: ResizeObserver;

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  get isScaleMode(): boolean {
    return this.question.presentation === 'HORIZONTAL';
  }

  ngAfterViewInit(): void {
    if (!this.isScaleMode) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScaleLayout();
    });
    this.resizeObserver.observe(this.el.nativeElement);
    this.updateScaleLayout();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  scrollStatusMessageIntoView() {
    if (this.statusMessageRef?.nativeElement) {
      // Focus the message for keyboard users and screen readers
      this.statusMessageRef.nativeElement.focus();
      // Then scroll it into view for visual users
      this.statusMessageRef.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  getMinTileWidth(): number {
    if (!this.question?.alternatives) return 120;
    return TextMeasurementUtil.calculateMinTileWidth(
      this.question.alternatives,
      this.question.id,
    );
  }

  hasQuotaError(alternativeId: string): boolean {
    const control = this.formGroup?.get(alternativeId);
    if (control?.errors?.['QuotaError']) {
      return true;
    }

    return this.quotaErrorAlternativeIds?.has(alternativeId) ?? false;
  }

  private updateScaleLayout(): void {
    const containerWidth = this.el.nativeElement.clientWidth;
    const count = this.question.alternatives?.length ?? 0;

    const fits = TextMeasurementUtil.fitsInSingleRow(
      this.question.alternatives,
      containerWidth,
    );

    this.scaleColumns = fits ? count : 1;
    this.cdr.detectChanges();
  }
}
